import { Callback } from "game-common/util"
import { Container, Graphics } from "pixi.js"

import { ClientGameLogic } from "../../../../client_game_logic"
import { stageViewport } from "../../../../canvas_layout"
import { Dialog, DialogOptions } from "./dialog"

export interface ModalProps extends DialogOptions {
    content: Graphics
    closeCallback?: Callback<boolean>
    fullScreen?: boolean
    onResize?: () => void
    // Internal lifecycle notification; unlike closeCallback, suppression never skips this.
    onClosed?: () => void
}
export class Modal {
    // Every modal up right now, whichever screen raised it (an item screen, the world map, help...).
    private static open = new Set<Modal>()

    /** True while any modal is up. One whose stage has since been torn down no longer counts. */
    static isAnyOpen = (): boolean => Array.from(Modal.open).some(modal => !modal.parent.destroyed)

    logic: ClientGameLogic
    parent: Container
    content: Graphics
    private props: ModalProps
    private dialog: Dialog
    width: number
    height: number
    suppressOnCloseCallback?: boolean
    closedClicked: boolean = false
    private closed: boolean = false
    private escapeSuspended: boolean = false

    constructor(logic: ClientGameLogic, parent: Container, props: ModalProps) {
        this.logic = logic
        this.parent = parent
        this.props = props
        this.resolveLayout()
    }

    private resolveLayout = (): Pick<DialogOptions, "x" | "y" | "width" | "height"> => {
        const viewport = stageViewport()
        const padding = this.props.padding ?? 0
        const bounds = this.props.content.getLocalBounds()
        this.width = this.props.fullScreen ? Math.max(0, viewport.width - padding * 2)
            : this.props.width ?? Math.max(0, bounds.x + bounds.width)
        this.height = this.props.fullScreen ? Math.max(0, viewport.height - padding * 2)
            : this.props.height ?? Math.max(0, bounds.y + bounds.height)
        return {
            width: this.width,
            height: this.height,
            x: this.props.fullScreen ? viewport.left : this.props.center
                ? viewport.left + (viewport.width - this.width - padding * 2) / 2 : this.props.x ?? 0,
            y: this.props.fullScreen ? viewport.top : this.props.center
                ? viewport.top + (viewport.height - this.height - padding * 2) / 2 : this.props.y ?? 0,
        }
    }

    private resize = () => {
        if (this.closed || !this.dialog) {
            return
        }
        this.dialog.layout(this.resolveLayout())
        this.props.onResize?.()
    }

    // Key DOWN only. The key-up matters because a sub-panel that borrows Escape (see suspendEscape)
    // hands it back the moment it closes on the key-down -- and the key-up that follows would
    // otherwise land here and take the whole screen down behind it.
    private closeListener = (key, down) => {
        if (!this.closed && this.dialog && key === "Escape" && down && !this.escapeSuspended) {
            this.closedClicked = true
            this.dialog.close()
        }
    }

    // A sub-panel opened INSIDE this modal (the trade screen's quantity picker) borrows Escape while
    // it is up, so backing out of the picker doesn't also tear down the screen behind it.
    suspendEscape = (suspended: boolean) => {
        this.escapeSuspended = suspended
    }

    private release = () => {
        if (this.closed) {
            return
        }
        this.closed = true
        Modal.open.delete(this)
        window.removeEventListener("samsara:canvas-resize", this.resize)
        // this.logic.playerController.enabledKeyboardAction(true)
        this.logic.playerController.enabledMouseAction(true)
        this.logic.clientRenderer.keyboard().removeListener(this.closeListener)
        this.props.onClosed?.()
        if (this.props.closeCallback && !this.suppressOnCloseCallback) {
            this.props.closeCallback(this.closedClicked)
        }
    }

    update = () => {
        if (this.closed) {
            return
        }
        if (this.dialog) {
            this.dialog.layout(this.resolveLayout())
            return
        }
        const createDialog = () => {
            const mouseOver = () => {
                if (this.props.onMouseOver) {
                    this.props.onMouseOver()
                }
                this.logic.enablePlayerMouseAction(false)
            }
            const mouseOut = () => {
                if (this.props.onMouseOut) {
                    this.props.onMouseOut()
                }
                this.logic.enablePlayerMouseAction(true)
            }
            const dialog = new Dialog(this.parent, {
                ...this.props,
                ...this.resolveLayout(),
                onMouseOver: mouseOver,
                onMouseOut: mouseOut,
                closeCallback: this.release,
                onCloseClick: () => {
                    this.closedClicked = true
                    dialog.close()
                },
            })

            dialog.update()

            return dialog
        }

        const dialog = createDialog()
        this.dialog = dialog
        Modal.open.add(this)
        if (this.props.fullScreen || this.props.center) {
            window.addEventListener("samsara:canvas-resize", this.resize)
        }
        // setup
        // this.logic.playerController.enabledKeyboardAction(false)
        this.logic.playerController.enabledMouseAction(false)
        if (!this.props.unescapeable) {
            this.logic.clientRenderer.keyboard().addListener(this.closeListener)
        }
    }

    close = () => {
        if (!this.dialog) {
            this.closed = true
            return
        }
        this.dialog.close()
    }
}
